import type { IOrder, IUser } from '../../../../Model/index.js'
import { canUseKronaOnOrder, canUseOtuTableOnOrder, canUseVisualisationOnOrder } from '../../../../Model/index.js'
import type { OrderContextActionsProps } from './OrderContextActionsWidget.tsx'
import { ProcessingTypes } from '../../../../Generated/ProcessingType.ts'
import { getDetectedSequencingPlatform, getPlatformProcessingTypeConflict } from '../../../../Util/sequencingPlatform.ts'

/**
 * Externalize checks if certain actions are available for an order.
 * Encapsulates the conditions as well as the "why can't I do this" message.
 *
 * As the checks need some context/state, they are passed into the actual check construction
 */
export type Check = (props: OrderContextActionsProps, user: IUser | undefined) => { check: () => boolean; message: string }

export const gOr = (g1: Check, g2: Check) => {
  return (props: OrderContextActionsProps, user: IUser | undefined) => {
    const g1c = g1(props, user)
    const g2c = g2(props, user)
    return {
      check: () => g1c.check() || g2c.check(),
      message: !g1c.check() ? g1c.message : g2c.message
    }
  }
}

export const gNeedsChecksum: Check = (props) => ({
  check: () => !!props.tokenChecksum,
  message: 'Checksum must be available for this action'
})

export const gStateDraft: Check = (props) => ({
  check: () => props.orderState === 'DRAFT',
  message: 'Only available for order drafts'
})

export const gOrderNotDone: Check = (props) => ({
  check: () => props.orderState !== 'DONE',
  message: 'Only available for incomplete orders'
})

export const gOrderDone: Check = (props) => ({
  check: () => props.orderState === 'DONE',
  message: 'Only available for completed orders'
})

export const gIsMetabarcoding: Check = (props) => ({
  check: () => props.order.processingType === ProcessingTypes.METABARCODING.key,
  message: 'Only available for metabarcoding orders'
})

export const gIsEDNA: Check = (props) => ({
  check: () => props.order.processingType === ProcessingTypes.EDNA.key,
  message: 'Only available for eDNA orders'
})

export const gPlatformSupportsProcessingType: Check = (props) => {
  const conflict = getPlatformProcessingTypeConflict({
    detectedSequencingPlatform: getDetectedSequencingPlatform(props.order, props.samplePools),
    sequencingPlatformOverride: props.order.sequencingPlatformOverride,
    processingType: props.order.processingType
  })
  return {
    check: () => !conflict,
    message: conflict ?? ''
  }
}

export const gUnclaimedLab: Check = (props) => ({
  check: () => !props.order.lab,
  message: 'Only available for unclaimed orders'
})

export const gUnclaimed: Check = (props) => ({
  check: () => !props.order.customer,
  message: 'Only available for unclaimed orders'
})

/*
 * The member-only gates all reduce to "is the viewer's org the order's lab or customer?". Extracted into
 * [orgMatches] / [isOrgMemberOfOrder] so these gates AND member-only UI elsewhere (e.g. the order-detail
 * file downloads in OrderDetailInfoWidget) share ONE definition and can't drift.
 *
 * The `!= null` guard is load-bearing: without it an anonymous viewer (`user?.org?.id === undefined`) on an
 * unclaimed order (lab/customer also undefined) would satisfy `undefined === undefined` and wrongly pass —
 * surfacing member-only actions to a share-link viewer that then 404/401 on the backend.
 */
const orgMatches = (user: IUser | undefined, orgId: string | null | undefined): boolean => user?.org?.id != null && user.org.id === orgId

/** Whether [user]'s org owns [order] (it is the lab or the customer). The one member-of-order predicate. */
export const isOrgMemberOfOrder = (user: IUser | undefined, order: Pick<IOrder, 'lab' | 'customer'>): boolean =>
  orgMatches(user, order.lab) || orgMatches(user, order.customer)

export const gIsLab: Check = (props, user) => ({
  check: () => orgMatches(user, props.order.lab),
  message: 'Only available for orders from your lab'
})

export const gIsCustomer: Check = (props, user) => ({
  check: () => orgMatches(user, props.order.customer),
  message: 'Only available for order drafts created by you'
})

export const gIsLabOrCustomer: Check = (props, user) => ({
  check: () => isOrgMemberOfOrder(user, props.order),
  message: 'Only available for orders owned by you'
})

export const gIsUnsubmitted: Check = (props) => ({
  check: () => !props.order.orderSubmission,
  message: 'Only available for unsubmitted orders'
})

export const gCanBeResubmitted: Check = (props) => ({
  check: () => !!props.order.orderPreprocessed && !props.order.orderPostprocessingStarted,
  message: 'Only available for orders that have been submitted and not yet postprocessed'
})

export const gOrderTrimmed: Check = (props) => ({
  check: () => props.order.sampleConfiguration === 'TRIMMED_SAMPLES' || props.order.sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES',
  message: 'Only available for orders with trimmed samples'
})

const INSIGHT_ON_ORDER_MESSAGE = 'Only available with an active INSIGHT license, or one shared by the processing lab'

export const gOtuTableAccess: Check = (props, user) => ({
  check: () => canUseOtuTableOnOrder(props.order, user),
  message: INSIGHT_ON_ORDER_MESSAGE
})

export const gKronaAccess: Check = (props, user) => ({
  check: () => canUseKronaOnOrder(props.order, user),
  message: INSIGHT_ON_ORDER_MESSAGE
})

/** Must agree with the INSIGHT menu group's gate, or the workspace entry vanishes from a group that is shown. */
export const gVisualisationAccess: Check = (props, user) => ({
  check: () => canUseVisualisationOnOrder(props.order, user),
  message: INSIGHT_ON_ORDER_MESSAGE
})
